import React, { useMemo } from "react";

import { mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import { FILLER_COLOR } from "../../../constants/fast";
import Button from "../../button/Button";
import { FAST_CHANNELS_ROUTE } from "../../routes/routes";

import type { JSX } from "react";

import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface CalendarDayColumnWrapperProps {
  children: React.ReactNode[];
  className: string;
  date: Date;
}

function CalendarDayColumnWrapper({
  children,
  className,
  date,
}: CalendarDayColumnWrapperProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const history = useHistory();
  const { channelId } = useParams<FASTChannelRoutesParams>();

  const currentDay = moment(date);
  const nextDay = currentDay.clone().add(1, "day");

  const isDSTTransitionDay = currentDay.isDST() !== nextDay.isDST();

  const updatedChildren = useMemo(() => {
    if (isDSTTransitionDay) {
      // add extra hour element to avoid layout shift when hour are 23 instead of 24 on DST day

      if (children?.[0] && Array.isArray(children[0])) {
        if (children[0].length === 23) {
          const lastChildOfFirst = children[0][children[0].length - 1];

          return [[...children[0], lastChildOfFirst], ...children.slice(1)];
        }

        // remove extra hour element to avoid layout shift when hour are 25 instead of 24 on DST day
        if (children[0].length === 25) {
          return [children[0].slice(0, -1), ...children.slice(1)];
        }
      }
    }

    return children;
  }, [children, isDSTTransitionDay]);

  if (isDSTTransitionDay) {
    const containerClassNames = classNames(className, "relative");

    const startTimeTimeline = currentDay.startOf("day").valueOf();
    const endTimeTimeline = startTimeTimeline + 24 * 60 * 60 * 1000;

    const handleClick = (): void => {
      history.push(
        `${FAST_CHANNELS_ROUTE}/${channelId}/scheduling?startTime=${startTimeTimeline}&endTime=${endTimeTimeline}`,
      );
    };

    return (
      <div className={containerClassNames}>
        {updatedChildren}
        <div
          className="absolute top-0 size-full"
          style={{ backgroundColor: FILLER_COLOR }}
        >
          <div className="flex flex-col items-center justify-center gap-[1.5625rem] p-[0.625rem]">
            <div className="flex items-center justify-center gap-[0.3125rem]">
              <div className="font-semibold">{t("daylightSavingTime")}</div>
              <div
                data-tooltip-content={t("daylightSavingTimeTooltip")}
                data-tooltip-id="calendar-date-cell"
              >
                <Icon path={mdiInformationOutline} size={0.9375} />
              </div>
            </div>
            <Button onClick={handleClick} size="sm" type="button">
              {t("accessSchedule")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <div className={className}>{updatedChildren}</div>;
}

export default CalendarDayColumnWrapper;
